import Config from '@/shared/Config'
import API from '@/shared/plugins/Api/API'
import qs from 'qs'
import urljoin from 'url-join'
import ACLS from './ACLS'
import Applications from './Applications.js'
import AuthenticationProviders from './AuthenticationProviders.js'
import Configuration from './Configuration.js'
import Groups from './Groups.js'
import Mails from './Mails'
import Metrics from './Metrics.js'
import Roles from './Roles.js'
import ServiceAccounts from './ServiceAccounts.js'
import Users from './Users.js'
import Vue from 'vue'

class IAM extends API {
  constructor (id) {
    super(id)
    this.metrics = new Metrics(id)
    this.authentication_providers = new AuthenticationProviders(id)
    this.roles = new Roles(id)
    this.applications = new Applications(id)
    this.users = new Users(id)
    this.groups = new Groups(id)
    this.configuration = new Configuration(id)
    this.service_accounts = new ServiceAccounts(id)
    this.mails = new Mails(id)
    this.acls = new ACLS(id)
    this.cacheAcls = {}
  }

  async getCacheKey (key) {
    if (!key) return null
    const config = await Config()
    return `${config.PROJECT_ID}::${key}`
  }

  async can (Service, Resource, Action, object, forcedSession = null, retry = 0) {
    const keyCache = await this.getCacheKey(!object && `${Service}::${Resource}::${Action}`)
    let data = null
    if (keyCache && !retry) { // When we retry, we do not want to get the cache to have latest acls so we always call the api
      this.cacheAcls[keyCache] = this.cacheAcls[keyCache] || this.request({
        method: 'POST',
        url: 'v4/opa/query',
        data: {
          Service,
          Resource,
          Action
        }
      })
      data = await this.cacheAcls[keyCache]
    } else {
      data = await this.request({
        method: 'POST',
        url: 'v4/opa/query',
        data: {
          Service,
          Resource,
          Action,
          Object: object
        }
      })
    }

    // For now we only use async for the project dashboard, we have a condition to check if forcedSession is null or not to identify if this function is called from the dashboard in core or not
    // Once we want to implement this also in all the services, we remove this condition and apply to all of the place where we call this function [DATATR-2003]
    if (forcedSession) {
      // Even if data.allow is false, if the user has acls to access in the session, we do the recursive call maximum 60 sec because opa call might return the incorrect acls just after the creation of the new project
      // This is to prevent the user could not access to the services even the have rights to access [DATATR-2003]
      if (!data.allow && retry < 13 && Vue.acl('', forcedSession).hasAccessTo(Service, Resource, Action)) {
        return new Promise(resolve => {
          setTimeout(async () => {
            retry++
            resolve(await this.can(Service, Resource, Action, object, forcedSession, retry))
          }, 5000)
        })
      } else {
        return data.allow
      }
    } else {
      return data.allow
    }
  }

  async reply (token) {
    return this.request({
      url: 'v4/reply',
      params: {
        redirect_uri: 'false',
        token_mode: 'cookie',
        authentication_provider: 'forepaas'
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  }

  async checksession (noReconnect = false) {
    return this.request({
      url: 'v4/checksession',
      retries: 0,
      noReconnect: noReconnect
    })
      .catch(err => {
        if (err.response?.status === 401 || err.response?.status === 403) return null
        throw err
      })
  }

  async authenticationProvidersStore () {
    return this.request({
      url: 'v4/store/authentication_providers'
    })
  }

  async applicationsStore () {
    return this.request({
      url: 'v4/store/applications'
    })
  }

  async preferences (clientId = null) {
    const config = await Config()
    return this.request({
      url: `v4/preferences/${clientId || config.APP_ID}`
    })
  }

  async login ({ login, email, password }) {
    const config = await Config()
    return this.request({
      url: 'v4/login',
      method: 'POST',
      retries: 0,
      data: {
        auth_mode: 'login_password',
        login,
        email,
        password,
        app_id: config.APP_ID
      }
    })
  }

  // client_id + redirect_uri make the mailed link return to that application
  async passwordReset ({ email, client_id, redirect_uri }) {
    const data = client_id && redirect_uri ? { email, client_id, redirect_uri } : { email }
    return this.request({
      url: 'v4/sendRenewRequest',
      method: 'POST',
      data
    })
  }

  async passwordRenew (token, newPassword) {
    return this.request({
      url: 'v4/passwordReset',
      method: 'POST',
      retries: 0,
      // A dead reset token is not an expired session
      noReconnect: true,
      data: {
        password: newPassword,
        token
      }
    })
  }

  async unblock (id) {
    return this.request({
      method: 'PUT',
      url: `v4/users/${id}/unblock`
    })
  }

  async loginRedirect (loginMode, forceAuth = false, clientId = null) {
    const config = await Config()
    const queryString = qs.stringify({
      authentication_provider: loginMode._id,
      app_id: clientId || config.APP_ID,
      redirect_uri: window.location.href,
      token_mode: 'cookie',
      force_auth: forceAuth,
      organization_id: config.ORGANIZATION_MODE ? config.ORGANIZATION_ID : undefined
    })
    const loginUrl = urljoin(config.IAM, '/v4/login', `?${queryString}`)
    window.location.href = loginUrl
  }

  // Send code to the user. For now it is used for only SMS
  async sendMFACode (type) {
    return this.request({
      method: 'post',
      retries: 0,
      url: `v4/mfa/send/${type}`,
      headers: {
        'Organization-Id': 'king'
      }
    })
  }

  // Used in the login page
  async validateMFACode ({ type, otp }) {
    return this.request({
      method: 'post',
      retries: 0,
      url: `v4/mfa/validate/${type}`,
      data: {
        otp
      },
      headers: {
        'Organization-Id': 'king'
      }
    })
  }

  async signup ({ email, password, name, tc_accepted, redirect_uri, tokenJoin, organizationId }) {
    return this.request({
      method: 'post',
      url: 'v4/signup',
      data: {
        email,
        password,
        name,
        tc_accepted,
        redirect_uri,
        tokenJoin,
        organizationId
      }
    })
  }

  // Check if the email is invited
  async canSignupEmail (email) {
    const signupRequest = await this.request({
      originalError: true,
      method: 'POST',
      url: 'v4/signup',
      data: {
        email
      }
    })

    if (signupRequest?.response?.data?.status === 400 && signupRequest?.response?.data?.error === 'MISSING_FIELD') return true
    return signupRequest?.response?.data?.error
  }

  async logout () {
    return this.request({
      method: 'delete',
      url: 'v4/logout'
    })
      .catch(err => {
        if (err.response.status === 400) return null
        throw err
      })
  }
}

export default IAM
